import { percentualParaTaxa, type AtualizarCentroDto, type CentroArvoreFormValores, type CentroNoResponse, type CriarCentroDto } from '@triade/contracts';
import { fracaoParaPercentualTexto } from '@/lib/format';
import { semZeros } from './tipos';

/**
 * Formulario do centro na arvore <-> DTOs da API. So converte texto: '' vira null (nao informado), virgula vira ponto. Nenhum
 * numero passa por `number`. Sintetico nao envia minutos nem area (a API recusa minutos e ignora a area).
 */

const vazioParaNulo = (texto: string): string | null => {
  const t = texto.trim();
  return t === '' ? null : t.replace(',', '.');
};
const opcao = <T extends string>(v: T | ''): T | null => (v === '' ? null : v);
/** Ociosidade digitada (%) -> fracao para a API; vazio = null (usa a da empresa). O formulario ja validou a faixa. */
const ociosidadeParaDto = (texto: string): string | null => {
  const t = texto.trim();
  return t === '' ? null : percentualParaTaxa(t);
};

export function formNovo(): CentroArvoreFormValores {
  return { identificador: '', nome: '', tipoNo: 'ANALITICO', natureza: 'PRODUTIVO', classificacao: 'TRANSFORMACAO', tipoTrabalho: '', areaM2: '', minutosDisponiveisMes: '', ociosidade: '', ativo: true };
}

export function formDoCentro(c: CentroNoResponse): CentroArvoreFormValores {
  const numero = (v: string | null) => (v === null ? '' : semZeros(v).replace('.', ','));
  return {
    identificador: c.identificador,
    nome: c.nome,
    tipoNo: c.tipoNo,
    natureza: c.natureza ?? '',
    classificacao: c.classificacao ?? '',
    tipoTrabalho: c.tipoTrabalho ?? '',
    areaM2: numero(c.areaM2),
    minutosDisponiveisMes: numero(c.minutosDisponiveisMes),
    ociosidade: fracaoParaPercentualTexto(c.ociosidade),
    ativo: c.ativo,
  };
}

/** Criar: o pai vem do botao "+ filho" (ou null = raiz). */
export function dtoCriar(v: CentroArvoreFormValores, parentId: string | null): CriarCentroDto {
  const analitico = v.tipoNo === 'ANALITICO';
  return {
    identificador: v.identificador.trim(),
    nome: v.nome.trim(),
    tipoNo: v.tipoNo,
    parentId,
    natureza: opcao(v.natureza),
    classificacao: analitico ? opcao(v.classificacao) : null,
    tipoTrabalho: analitico ? opcao(v.tipoTrabalho) : null,
    areaM2: analitico ? vazioParaNulo(v.areaM2) : null,
    minutosDisponiveisMes: analitico ? vazioParaNulo(v.minutosDisponiveisMes) : null,
    ativo: v.ativo,
  };
}

/** Editar: manda os campos do formulario inteiros (o pai muda so por "Mover para…"). */
export function dtoAtualizar(v: CentroArvoreFormValores): AtualizarCentroDto {
  const analitico = v.tipoNo === 'ANALITICO';
  return {
    identificador: v.identificador.trim(),
    nome: v.nome.trim(),
    tipoNo: v.tipoNo,
    natureza: opcao(v.natureza),
    classificacao: analitico ? opcao(v.classificacao) : null,
    tipoTrabalho: analitico ? opcao(v.tipoTrabalho) : null,
    areaM2: analitico ? vazioParaNulo(v.areaM2) : null,
    minutosDisponiveisMes: analitico ? vazioParaNulo(v.minutosDisponiveisMes) : null,
    ociosidade: analitico ? ociosidadeParaDto(v.ociosidade) : null,
    ativo: v.ativo,
  };
}

/** Limpa os campos que o tipo escolhido nao aceita (o formulario mostra vazio, nao um valor escondido). */
export function limparParaTipo(v: CentroArvoreFormValores): CentroArvoreFormValores {
  if (v.tipoNo === 'SINTETICO') return { ...v, classificacao: '', tipoTrabalho: '', areaM2: '', minutosDisponiveisMes: '', ociosidade: '' };
  return v;
}
